"use strict";
import React, { useState } from "react";
import classNames from "classnames";
import { Stack } from "../Stack/Stack";
import { Text } from "../Text";
import { ChevronLeft, ChevronRight } from "../../icons";
import * as styles from "./DatePicker-css";
import { CalendarDates, getCalendarDate, getMonth, months, weekdays } from "./DatePickerHelperFunctions";
import IconButton from "../IconButton/IconButton";
import Link from "../Link/Link";
function DatePicker(props) {
  const { singleSelect, startDate, selectStartDate, endDate, selectEndDate, minDate, maxDate } = props;
  const today = props.today || getCalendarDate(new Date());
  const currentDate = endDate && endDate.year < today.year + 100 ? endDate : startDate || today;
  const [currentMonth, setCurrentMonth] = useState(currentDate.month);
  const [currentYear, setCurrentYear] = useState(currentDate.year);
  const [activePicker, setActivePicker] = useState("d");
  const month = getMonth(currentYear, currentMonth);
  const isSelectable = (date) => {
    if (minDate && CalendarDates.lt(date, minDate)) {
      return false;
    }
    if (maxDate && CalendarDates.gt(date, maxDate)) {
      return false;
    }
    if (!minDate && !maxDate && !props.allowPastSelection && CalendarDates.lt(date, today)) {
      return false;
    }
    return true;
  };
  const isSelectedDate = (date) => {
    if (startDate && CalendarDates.eq(date, startDate)) {
      return true;
    }
    if (endDate && CalendarDates.eq(date, endDate)) {
      return true;
    }
    return false;
  };
  const isInSelectedRange = (date) => {
    if (startDate && endDate) {
      return CalendarDates.lt(startDate, date) && CalendarDates.lt(date, endDate);
    }
    return false;
  };
  const isStartOfSelection = (date) => {
    if (startDate && endDate) {
      return CalendarDates.eq(date, startDate);
    }
    return false;
  };
  const isEndOfSelection = (date) => {
    if (startDate && endDate) {
      return CalendarDates.eq(date, endDate);
    }
    return false;
  };
  const isToday = (date) => {
    return CalendarDates.eq(date, today);
  };
  const navigate = (direction) => {
    switch (activePicker) {
      case "d": {
        const updatedMonthIndex = currentMonth - 1 + direction;
        setCurrentMonth((updatedMonthIndex + 12) % 12 + 1);
        if (updatedMonthIndex < 0 || updatedMonthIndex > 11) {
          setCurrentYear(currentYear + direction);
        }
        return;
      }
      case "m": {
        setCurrentYear(currentYear + direction);
        return;
      }
      case "y": {
        setCurrentYear(currentYear + direction * 12);
        return;
      }
    }
  };
  const updateSelection = (date) => {
    if (!isSelectable(date)) {
      return;
    }
    if (startDate && CalendarDates.eq(date, startDate)) {
      selectStartDate(void 0);
      selectEndDate == null ? void 0 : selectEndDate(void 0);
      return;
    }
    if (endDate && CalendarDates.eq(date, endDate)) {
      selectEndDate == null ? void 0 : selectEndDate(void 0);
      return;
    }
    if (singleSelect) {
      selectStartDate(date);
      return;
    }
    if (!startDate || CalendarDates.lt(date, startDate)) {
      selectStartDate(date);
      return;
    }
    selectEndDate == null ? void 0 : selectEndDate(date);
  };
  return /* @__PURE__ */ React.createElement("div", { className: styles.datePickerWrapper }, /* @__PURE__ */ React.createElement(Stack, { direction: "horizontal", align: "center", padding: "xs", className: styles.navigation }, /* @__PURE__ */ React.createElement(IconButton, { icon: /* @__PURE__ */ React.createElement(ChevronLeft, null), onClick: () => navigate(-1) }), /* @__PURE__ */ React.createElement(
    Link,
    {
      className: styles.navTitle,
      onClick: () => {
        setActivePicker(activePicker == "d" ? "m" : "y");
      }
    },
    /* @__PURE__ */ React.createElement(Text, { size: "s", strong: true }, activePicker == "y" && `${currentYear - currentYear % 12} - ${currentYear - currentYear % 12 + 11}`, activePicker == "m" && `${currentYear}`, activePicker == "d" && `${month.name} ${currentYear}`)
  ), /* @__PURE__ */ React.createElement(IconButton, { icon: /* @__PURE__ */ React.createElement(ChevronRight, null), onClick: () => navigate(1) })), (() => {
    switch (activePicker) {
      case "d":
        return /* @__PURE__ */ React.createElement(Stack, null, /* @__PURE__ */ React.createElement(Stack, { direction: "horizontal", className: styles.dayTitles }, weekdays.map((weekday) => /* @__PURE__ */ React.createElement(Text, { key: weekday, className: styles.day, size: "xs", strong: true }, weekday.slice(0, 3)))), /* @__PURE__ */ React.createElement(Stack, { className: styles.datesGrid }, [...Array(weekdays.indexOf(month.firstWeekday))].map((_, index) => /* @__PURE__ */ React.createElement("div", { key: `blank${index}` })), [...Array(month.numberOfDays)].map((_, index) => {
          const date = {
            year: currentYear,
            month: currentMonth,
            day: index + 1
          };
          return /* @__PURE__ */ React.createElement(
            Link,
            {
              key: date.day,
              className: classNames(
                styles.dateWrapper,
                isInSelectedRange(date) && styles.selectedRange,
                isStartOfSelection(date) && styles.startOfSelection,
                isEndOfSelection(date) && styles.endOfSelection
              ),
              onClick: () => updateSelection(date)
            },
            /* @__PURE__ */ React.createElement(
              Text,
              {
                strong: true,
                size: "s",
                className: classNames(
                  styles.date,
                  !isInSelectedRange(date) && (isSelectable(date) ? isToday(date) && styles.currentDate : styles.disabledDate),
                  isSelectedDate(date) && styles.selected
                )
              },
              date.day
            )
          );
        })));
      case "m":
        return /* @__PURE__ */ React.createElement(Stack, { className: styles.monthSelector }, months.map((name, index) => {
          const month2 = index + 1;
          return /* @__PURE__ */ React.createElement(
            Link,
            {
              key: month2,
              onClick: () => {
                setCurrentMonth(month2);
                setActivePicker("d");
              }
            },
            /* @__PURE__ */ React.createElement(Text, { strong: true, size: "s" }, name.slice(0, 3))
          );
        }));
      case "y":
        return /* @__PURE__ */ React.createElement(Stack, { className: styles.yearSelector }, [...Array(12).keys()].map((index) => {
          const year = index + currentYear - currentYear % 12;
          return /* @__PURE__ */ React.createElement(
            Link,
            {
              key: index,
              onClick: () => {
                setCurrentYear(year);
                setActivePicker("m");
              }
            },
            /* @__PURE__ */ React.createElement(Text, { strong: true, size: "s" }, year)
          );
        }));
      default:
        return null;
    }
  })());
}
export { DatePicker };
